import { Tag } from 'antd'

import { childUsers } from './constant'
import { dayJs } from './dayjs'
import { entries, isEqual, ternary } from './javascript'
import { sidebarMenus } from '../components/layout/sidebar.description'
import { countryLabels } from '../components/userManagement/addressData'

const addressFormat = data => {
  if (!data?.pincode && !data?.address) {
    return '-'
  }

  // Check if address already contains state, city, pincode, and country (case-insensitive)
  const addressLower = data?.address?.toLowerCase() || ''
  const hasState =
    data?.state && addressLower.includes(data?.state?.toLowerCase())
  const hasCity = data?.city && addressLower.includes(data?.city?.toLowerCase())
  const hasPincode =
    data?.pincode && addressLower.includes(data?.pincode?.toLowerCase())
  const hasCountry =
    data?.country &&
    addressLower.includes(countryLabels?.[data?.country]?.toLowerCase())

  // If address already contains all existing fields, return it as is
  const allFieldsPresent =
    (!data?.state || hasState) &&
    (!data?.city || hasCity) &&
    (!data?.pincode || hasPincode) &&
    (!data?.country || hasCountry)
  if (data?.address && allFieldsPresent) {
    return data.address
  }

  // Build address string with missing components
  let addressParts = []

  if (data?.address) {
    addressParts.push(data.address)
  }

  if (data?.state && !hasState) {
    addressParts.push(data.state)
  }

  if (data?.city && !hasCity) {
    addressParts.push(data.city)
  }
  if (data?.country && !hasCountry) {
    addressParts.push(countryLabels?.[data?.country])
  }

  if (data?.pincode && !hasPincode) {
    addressParts.push(data.pincode)
  }

  return data?.address
    ? `${data?.address}${data?.pincode ? ', ' + data?.pincode : ''}`
    : addressParts.length > 0
      ? addressParts.join(', ')
      : '-'
}

const nameParam = ({ roleId, searchByEmail }) =>
  ternary(
    searchByEmail,
    'emailId',
    ternary(childUsers.includes(roleId), 'name', 'businessName'),
  )

const getAddEditRoles = pathName =>
  sidebarMenus.find(({ key }) => isEqual(key, pathName))?.addEdit || []

const apiParams = ({ params, pageNo }) => {
  let tempParams = ''
  if (pageNo) {
    tempParams += `${pageNo}`
  }
  entries(params)?.forEach(([key, value], i) => {
    tempParams += `${ternary(isEqual(i, 0), '?', '&')}${key}=${value}`
  })
  return tempParams
}

const dateDaysDifference = (date1, date2) => {
  const startDate = dayJs(date1).startOf('day')
  const endDate = dayJs(date2).startOf('day')
  return endDate.diff(startDate, 'day')
}

const dateToStringPayload = date =>
  date ? dayJs(date)?.format('DD/MM/YYYY') : ''

const getBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

const inventoryTags = ({ t, inventoryType, text }) => {
  const tagFn = ({ type }) => {
    switch (type) {
      case 'PROCESSED_ACTUAL':
      case 'Estimated':
        return (
          <Tag
            color="#354e8b"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_ProcessedActual')}
          </Tag>
        )
      case 'LEFT_OVER':
      case 'Left Over':
        return (
          <Tag
            color="#ff4d4f"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_LeftOver')}
          </Tag>
        )
      case 'BUCKET_NAME':
        return (
          <Tag
            color="#6686d3"
            className="tag-style text-center mb-8 white-color"
          >
            {t(text)}
          </Tag>
        )
      case 'SCRAP_RECOVERED':
      case 'Scrap Recovered':
        return (
          <Tag
            color="#add8e6"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_ScrapRecovered')}
          </Tag>
        )
      case 'ELV_RECOVERED':
      case 'ELV Recovered':
        return (
          <Tag
            color="#add8e6"
            className="tag-style text-center mb-8 black-color"
          >
            {t('inv_ELVRecovered')}
          </Tag>
        )
      case 'AT_COLLECTION_CENTER':
      case 'At Collection Center':
        return (
          <Tag
            color="#ffcdcd"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_AtCollectionCenter')}
          </Tag>
        )
      case 'At Direct Individual':
        return (
          <Tag
            color="#f4c095"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_AtDirectIndividual')}
          </Tag>
        )
      case 'At Direct Dealer':
        return (
          <Tag
            color="#a8e6cf"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_AtDirectDealer')}
          </Tag>
        )
      case 'IN_TRANSIT':
      case 'In Transit':
        return (
          <Tag
            color="#aaacff"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_InTransit')}
          </Tag>
        )
      case 'OEM_RECOVERED':
      case 'OEM Recovered':
        return (
          <Tag
            color="#add8e6"
            className="tag-style text-center mb-8 black-color"
          >
            {t('inv_OEMRecovered')}
          </Tag>
        )
      case 'PROCESSED':
      case 'Processed':
        return (
          <Tag
            color="#87ad4a"
            className="tag-style text-center mb-8 black-color"
          >
            {t('inv_Processed')}
          </Tag>
        )
      case 'PROCESSED_CVS_GENERATED':
      case 'Processed and CVS Submitted':
        return (
          <Tag
            color="#89de00"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_ProcessedAndCVSSubmitted')}
          </Tag>
        )
      case 'MATERIAL_RECOVERED':
      case 'Material Recovered':
        return (
          <Tag
            color="#add8e6"
            className="tag-style text-center mb-8 black-color"
          >
            {t('inv_MaterialRecovered')}
          </Tag>
        )
      case 'PART_RECOVERED':
      case 'Part Recovered':
        return (
          <Tag
            color="#add8e6"
            className="tag-style text-center mb-8 black-color"
          >
            {t('tag_PartRecovered')}
          </Tag>
        )
      case 'RECYCLE_REQUESTED':
      case 'Recycle Requested':
        return (
          <Tag
            color="#2eb62e"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_RecycleRequest')}
          </Tag>
        )
      case 'PRE_RECYCLE':
      case 'Pre Recycle':
        return (
          <Tag
            color="#90ee90"
            className="tag-style text-center mb-8  black-color"
          >
            {t('tag_PreRecycle')}
          </Tag>
        )
      case 'POST_RECYCLE':
      case 'Post Recycle':
        return (
          <Tag
            color="#008000"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_PostRecycle')}
          </Tag>
        )
      case 'REFURBISHMENT_REQUESTED':
      case 'Refurbishment Requested':
        return (
          <Tag
            color="#ff5a00"
            className="tag-style text-center mb-8  black-color"
          >
            {t('tag_RecycleRequest')}
          </Tag>
        )
      case 'PRE_REFURBISHMENT':
      case 'POST_REFURBISHMENT_VIRGIN':
      case 'Pre Refurbishment':
        return (
          <Tag
            color="#90ee90"
            className="tag-style text-center mb-8  black-color"
          >
            {t('tag_PreRecycle')}
          </Tag>
        )
      case 'POST_REFURBISHMENT':
        return (
          <Tag
            color="#ff5a00"
            className="tag-style text-center mb-8  black-color"
          >
            {t('tag_PostRecycle')}
          </Tag>
        )
      case 'FINAL_PRODUCT':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_FinalProduct')}
          </Tag>
        )
      case 'Forged Parts':
      case 'FORGED_PARTS':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_ForgedParts')}
          </Tag>
        )
      case 'Machined Parts':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_MachinedParts')}
          </Tag>
        )
      case 'BLANKED_PARTS':
      case 'Blanked Parts':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_BlankedParts')}
          </Tag>
        )
      case 'STAMPED_PRESSED_PARTS':
      case 'Stamped/Pressed Parts':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_StampedParts')}
          </Tag>
        )
      case 'PAINTED_PARTS':
      case 'Painted Parts':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_PaintedParts')}
          </Tag>
        )
      case 'Unpainted Part':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_UnpaintedPart')}
          </Tag>
        )
      case 'Ready To Use':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_ReadyToUse')}
          </Tag>
        )
      case 'BARS':
        return (
          <Tag
            color="#a1a7ad"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_Bars')}
          </Tag>
        )
      case 'Pre Part Production':
      case 'PRE_PART_PRODUCTION':
        return (
          <Tag
            color="#a1a7ad"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_PrePartProduction')}
          </Tag>
        )
      case 'Ready To Sell':
      case 'READY_TO_SALES':
      case 'FORGED_PARTS_READY':
      case 'POST_REFURBISHMENT_READY':
        return (
          <Tag
            color="#c85c51"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_ReadyToSale')}
          </Tag>
        )
      case 'Sold':
      case 'SOLD':
      case 'SOLD_REFURBISHMENT':
      case 'FORGED_PARTS_SOLD':
        return (
          <Tag
            color="#4e90cc"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_Sold')}
          </Tag>
        )
      case '3RD_PARTY_RECYCLE':
      case '3rd Party Recycle':
        return (
          <Tag
            color="#46647e"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_3rdPartyRecycle')}
          </Tag>
        )
      case 'REUSE':
      case 'Reuse':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_Reuse')}
          </Tag>
        )
      case 'DESTROY':
      case 'Destroy':
        return (
          <Tag
            color="#c64d42"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_Destroy')}
          </Tag>
        )
      case 'REUSE_REQUEST':
      case 'Reuse Request':
        return (
          <Tag
            color="#547a1d"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_ReuseRequest')}
          </Tag>
        )
      case 'DESTRUCTION_REQUEST':
      case 'Destruction Request':
        return (
          <Tag
            color="#c64d42"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_DestructionRequest')}
          </Tag>
        )
      case 'REFURBISH':
      case 'Refurbish':
        return (
          <Tag
            color="#fd7447"
            className="tag-style text-center mb-8  black-color"
          >
            {t('inv_Refurbish')}
          </Tag>
        )
      case 'SELF_RECYCLE':
      case 'Self Recycle':
        return (
          <Tag
            color="#6ecb6e"
            className="tag-style text-center mb-8  black-color"
          >
            {t('inv_SelfRecycle')}
          </Tag>
        )
      case 'DISCARDED':
      case 'discarded':
        return (
          <Tag
            color="#c64d42"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_Discarded')}
          </Tag>
        )
      case 'TO_BE_SHREDDED':
        return (
          <Tag
            color="#ff9a00"
            className="tag-style text-center mb-8 white-color"
          >
            {t('job_ToBeShredded')}
          </Tag>
        )
      case 'POST_SHREDDING':
        return (
          <Tag
            color="#ff5a00"
            className="tag-style text-center mb-8 white-color"
          >
            {t('job_PostShredding')}
          </Tag>
        )
      case 'POST_GRINDING':
      case 'Post Grinding':
        return (
          <Tag
            color="#46647e"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tab_PostGrinding')}
          </Tag>
        )

      case 'PRE_COMPOUNDING':
      case 'Pre Compounding':
        return (
          <Tag
            color="#ff9a00"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_PreCompounding')}
          </Tag>
        )
      case 'POST_COMPOUNDING':
        return (
          <Tag
            color="#c85c51"
            className="tag-style text-center mb-8 white-color"
          >
            {t('tag_PostCompounding')}
          </Tag>
        )

      case 'POST_SORTING':
      case 'Post Sorting':
        return (
          <Tag
            color="#6fc508f2"
            className="tag-style text-center mb-8 white-color"
          >
            {t('job_PostSorting')}
          </Tag>
        )

      case 'PRE_PRODUCTION':
      case 'Pre-Production':
      case 'Pre Production':
        return (
          <Tag
            color="#90ee90"
            className="tag-style text-center mb-8  black-color"
          >
            {t('tag_PreProduction')}
          </Tag>
        )
      case 'OPERATIONAL_USER':
        return (
          <Tag
            color="#d8d8d8"
            className="tag-style text-center mb-8  black-color"
          >
            {t('user_OperationsUser')}
          </Tag>
        )
      case 'DATA_ENTRY_USER':
        return (
          <Tag
            color="#ffb34c"
            className="tag-style text-center mb-8  black-color"
          >
            {t('user_DataEntryUser')}
          </Tag>
        )
      case 'Used':
      case 'USED':
        return (
          <Tag
            color="#5f8fbf"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_Used')}
          </Tag>
        )
      case 'Available':
      case 'AVAILABLE':
        return (
          <Tag
            color="#5f8fbf"
            className="tag-style text-center mb-8 white-color"
          >
            {t('inv_Available')}
          </Tag>
        )
      case 'Dispatched':
      case 'DISPATCHED':
        return (
          <Tag
            color="#90ee90"
            className="tag-style text-center mb-8  black-color"
          >
            {t('rpt_Dispatched')}
          </Tag>
        )
      default:
        return null
    }
  }
  return tagFn({ type: inventoryType })
}

export {
  addressFormat,
  nameParam,
  getAddEditRoles,
  apiParams,
  dateDaysDifference,
  getBase64,
  inventoryTags,
  dateToStringPayload,
}
